/**
 * The filter bar under the search dropdown's app tab strip (需求 FR-14 §5.3.2 ③).
 *
 * ## Why a popover is possible here after all
 *
 * The first version drew the editor **inline**, replacing the result list, on
 * the premise that "the dropdown closes as soon as focus leaves the input, so a
 * popover is impossible". That premise was wrong, and it cost the feature its
 * usability: with nothing allowed to take focus, a picker's candidate search had
 * to hijack the omnibar field, which is where the lost-caret bug came from.
 *
 * Two things make the popover work:
 *
 * 1. The dropdown's `open` is **controlled** by the workbench, and Base UI hands
 *    `onOpenChange` a `reason` from a closed set. So the host can simply refuse
 *    the ambient closes (`focus-out`, `outside-press`) while a filter popover is
 *    up — the same guard the profile card already uses for platform overlays
 *    (见 `contacts` `user-profile-card.tsx` `AMBIENT_CLOSE`).
 * 2. Focus is therefore free to move in here, which is what lets the picker own
 *    a real `<input>` instead of borrowing the omnibar's.
 *
 * The one thing the host still has to do is put the caret back in the omnibar
 * when a panel closes (见 `header-search.tsx`); Base UI restores focus to the
 * trigger, which is a chip, and typing has to keep working.
 */
import type {
  ResolvedSearchFilter,
  SearchFilterOption,
  SearchFilterValue,
} from '@hibro/shared';
import {
  Calendar,
  cn,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
} from '@hibro/ui-client';
import type { ReactNode } from 'react';

import { useTranslator } from '../../i18n/i18n';
import {
  DATE_PRESETS,
  type DatePreset,
  datePresetRange,
  dateRangeSelection,
  dateRangeValue,
  filterChipSummary,
  togglePickerId,
} from '../../search/filters';
import {
  focusDidNotLeave,
  isPhantomFocusOut,
} from '../../search/search-contract';
import { SearchPersonAffix } from '../../search/search-person-affix';
import type { FilterOptionsState } from '../../search/use-filter-options';

export interface SearchFilterBarProps {
  filters: readonly ResolvedSearchFilter[];
  values: Readonly<Record<string, SearchFilterValue>>;
  /** Id of the filter whose panel is open, or null. */
  openFilterId: string | null;
  onOpenFilter: (id: string | null) => void;
  onChange: (id: string, value: SearchFilterValue | null) => void;
  /** Candidate rows for the open `picker`; empty otherwise. */
  options: FilterOptionsState;
  /** The open picker's own search text. */
  query: string;
  onQueryChange: (next: string) => void;
  /** Resolves a filter's `%key%` title against its declaring module. */
  resolveTitle: (moduleId: string, title: string) => string;
}

/** The chip row. Drawn only when the active app tab declared filters. */
export function SearchFilterBar({
  filters,
  values,
  openFilterId,
  onOpenFilter,
  onChange,
  options,
  query,
  onQueryChange,
  resolveTitle,
}: SearchFilterBarProps): ReactNode {
  if (filters.length === 0) {
    return null;
  }
  return (
    <div className="flex shrink-0 items-center gap-1.5 overflow-x-auto border-b border-(--app-color-border) px-3 py-1.5">
      {filters.map((filter) => (
        <SearchFilterChip
          key={filter.id}
          filter={filter}
          value={values[filter.id]}
          open={openFilterId === filter.id}
          onOpenChange={(next) => onOpenFilter(next ? filter.id : null)}
          onChange={(value) => onChange(filter.id, value)}
          options={options}
          query={query}
          onQueryChange={onQueryChange}
          resolveTitle={resolveTitle}
        />
      ))}
    </div>
  );
}

function SearchFilterChip({
  filter,
  value,
  open,
  onOpenChange,
  onChange,
  options,
  query,
  onQueryChange,
  resolveTitle,
}: {
  filter: ResolvedSearchFilter;
  value: SearchFilterValue | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (value: SearchFilterValue | null) => void;
  options: FilterOptionsState;
  query: string;
  onQueryChange: (next: string) => void;
  resolveTitle: (moduleId: string, title: string) => string;
}): ReactNode {
  const { t, locale } = useTranslator();
  const summary = filterChipSummary(value);
  const label = resolveTitle(filter.moduleId, filter.title);
  return (
    <Popover
      open={open}
      onOpenChange={(next, details) => {
        // Completing a date range makes react-day-picker repaint the grid and
        // replace the focused day button, which reads here as `focus-out` with
        // focus on nobody. Closing on that costs the user the panel mid-gesture
        // — and, because it releases the dropdown's hold, the dropdown too
        // (见 `isPhantomFocusOut`).
        const held =
          !next && isPhantomFocusOut(details.reason, focusDidNotLeave());
        if (held) {
          return;
        }
        onOpenChange(next);
      }}
    >
      <PopoverTrigger
        // Out of the tab order like every control in the popup: `Tab` walks the
        // app tab strip here (需求 §5.2). Clicking still focuses it, which is
        // what lets the popover manage focus properly.
        tabIndex={-1}
        className={cn(
          'flex h-6 shrink-0 cursor-pointer items-center gap-1 rounded-full border px-2 text-[11px] leading-4 outline-none',
          summary || open
            ? 'border-(--app-color-accent) text-(--app-color-accent)'
            : 'border-(--app-color-border) text-(--app-color-text-faint)',
        )}
      >
        <span>{label}</span>
        {summary ? (
          <span className="font-medium">
            {summary.kind === 'count'
              ? t('workbench.search.filter.count', { count: summary.count })
              : formatRange(summary.from, summary.to, locale)}
          </span>
        ) : null}
        <Icon
          className="size-3"
          spec={{ type: 'builtin', id: 'chevron-down' }}
        />
      </PopoverTrigger>
      {/* Sized by content rather than the part's default `w-72`: the calendar
          and the candidate list want different widths, and neither wants the
          default 1rem padding. */}
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-auto max-w-80 min-w-56 p-0"
      >
        <div className="flex items-center justify-between px-3 py-1.5 text-[11px] leading-4 text-(--app-color-text-muted)">
          <span>{label}</span>
          {value ? (
            <button
              type="button"
              className="cursor-pointer text-(--app-color-text-faint) hover:text-(--app-color-text-primary)"
              onClick={() => onChange(null)}
            >
              {t('workbench.search.filter.clear')}
            </button>
          ) : null}
        </div>
        {filter.type === 'dateRange' ? (
          <DateRangePanel
            value={value}
            onChange={onChange}
            onDone={() => onOpenChange(false)}
          />
        ) : (
          <PickerPanel
            filter={filter}
            value={value}
            onChange={onChange}
            options={options}
            query={query}
            onQueryChange={onQueryChange}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

function DateRangePanel({
  value,
  onChange,
  onDone,
}: {
  value: SearchFilterValue | undefined;
  onChange: (value: SearchFilterValue | null) => void;
  onDone: () => void;
}): ReactNode {
  const { t } = useTranslator();
  const selected = dateRangeSelection(value);
  return (
    <div className="flex flex-col pb-2">
      <div className="flex items-center gap-1.5 px-3 pb-1">
        {DATE_PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            className="cursor-pointer rounded-full border border-(--app-color-border) px-2 py-0.5 text-[11px] leading-4 text-(--app-color-text-faint) hover:border-(--app-color-accent) hover:text-(--app-color-accent)"
            onClick={() => {
              onChange({ type: 'dateRange', ...datePresetRange(preset) });
              // A preset is a complete answer, so the panel gets out of the way
              // — unlike the calendar, where the first click is half a range.
              onDone();
            }}
          >
            {t(presetLabelKey(preset))}
          </button>
        ))}
      </div>
      <Calendar
        mode="range"
        className="mx-auto"
        selected={
          selected.from ? { from: selected.from, to: selected.to } : undefined
        }
        onSelect={(range) => {
          const next = dateRangeValue(range?.from, range?.to);
          onChange(
            next.type === 'dateRange' &&
              next.from === undefined &&
              next.to === undefined
              ? null
              : next,
          );
        }}
      />
    </div>
  );
}

function presetLabelKey(preset: DatePreset): string {
  switch (preset) {
    case 'today':
      return 'workbench.search.filter.today';
    case 'last7':
      return 'workbench.search.filter.last7';
    default:
      return 'workbench.search.filter.last30';
  }
}

function PickerPanel({
  filter,
  value,
  onChange,
  options,
  query,
  onQueryChange,
}: {
  filter: ResolvedSearchFilter;
  value: SearchFilterValue | undefined;
  onChange: (value: SearchFilterValue | null) => void;
  options: FilterOptionsState;
  query: string;
  onQueryChange: (next: string) => void;
}): ReactNode {
  const { t } = useTranslator();
  const picked = new Set(value?.type === 'picker' ? value.ids : []);
  return (
    <div className="flex flex-col">
      {/* Its own field, not the omnibar's. That is the whole point of the
          popover: the caret can live here without the dropdown dismissing. */}
      <div className="px-2 pb-1.5">
        <input
          autoFocus
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder={t('workbench.search.filter.optionsPlaceholder')}
          className="h-7 w-full rounded-md border border-(--app-color-border) bg-(--app-color-bg-surface) px-2 text-[12px] leading-5 text-(--app-color-text-primary) outline-none placeholder:text-(--app-color-text-muted) focus:border-(--app-color-accent)"
        />
      </div>
      <PickerRows
        filter={filter}
        value={value}
        onChange={onChange}
        options={options}
        picked={picked}
      />
    </div>
  );
}

function PickerRows({
  filter,
  value,
  onChange,
  options,
  picked,
}: {
  filter: ResolvedSearchFilter;
  value: SearchFilterValue | undefined;
  onChange: (value: SearchFilterValue | null) => void;
  options: FilterOptionsState;
  picked: ReadonlySet<string>;
}): ReactNode {
  const { t } = useTranslator();
  if (options.error) {
    // Visible, not folded into an empty list: a candidate source that is down
    // must not look like one with nothing to offer (见 `no-implicit-fallbacks`).
    return (
      <div className="px-3 py-3 text-[11px] text-(--app-color-danger)">
        {t('workbench.search.filter.optionsFailed')}
      </div>
    );
  }
  if (options.loading && options.options.length === 0) {
    return (
      <div className="flex items-center justify-center py-6">
        <Spinner className="size-4" />
      </div>
    );
  }
  if (options.options.length === 0) {
    return (
      <div className="px-3 py-3 text-center text-[11px] text-(--app-color-text-faint)">
        {t('workbench.search.filter.noOptions')}
      </div>
    );
  }
  return (
    <div className="flex max-h-64 flex-col overflow-y-auto pb-1">
      {options.options.map((option) => (
        <PickerRow
          key={option.id}
          option={option}
          checked={picked.has(option.id)}
          onToggle={() =>
            onChange(togglePickerId(value, option.id, filter.multiple === true))
          }
        />
      ))}
    </div>
  );
}

function PickerRow({
  option,
  checked,
  onToggle,
}: {
  option: SearchFilterOption;
  checked: boolean;
  onToggle: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      role="option"
      aria-selected={checked}
      onClick={onToggle}
      className="flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left outline-none hover:bg-(--app-color-bg-muted)"
    >
      {option.icon ? (
        <Icon className="size-5 shrink-0" spec={option.icon} />
      ) : null}
      <span className="flex min-w-0 flex-auto flex-col">
        <span className="flex min-w-0 items-center">
          <span className="truncate text-[13px] leading-5">{option.label}</span>
          {option.personId ? (
            <SearchPersonAffix personId={option.personId} />
          ) : null}
        </span>
        {option.subtitle ? (
          <span className="truncate text-[11px] leading-4 text-(--app-color-text-faint)">
            {option.subtitle}
          </span>
        ) : null}
      </span>
      {checked ? (
        <Icon
          className="size-4 shrink-0 text-(--app-color-accent)"
          spec={{ type: 'builtin', id: 'check' }}
        />
      ) : null}
    </button>
  );
}

/** `9/1 – 9/9`, with an open end shown as a bare bound. */
function formatRange(
  from: Date | undefined,
  to: Date | undefined,
  locale: string,
): string {
  const short = (date: Date): string =>
    date.toLocaleDateString(locale, { month: 'numeric', day: 'numeric' });
  if (from && to) {
    return from.toDateString() === to.toDateString()
      ? short(from)
      : `${short(from)} – ${short(to)}`;
  }
  return from ? `${short(from)} –` : to ? `– ${short(to)}` : '';
}
